<template>
  <div class="date-range-picker">
    <!-- Calendar Header with Navigation -->
    <div class="calendar-container">
      <!-- Left Calendar -->
      <div class="calendar">
        <div class="calendar-header">
          <div class="nav-buttons">
            <b-button
              variant="link"
              class="nav-btn"
              @click="previousYear(0)"
            >
              <feather-icon
                icon="ChevronsLeftIcon"
                size="18"
              />
            </b-button>
            <b-button
              variant="link"
              class="nav-btn"
              @click="previousMonth(0)"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </b-button>
          </div>
          <div class="month-year">
            {{ formatMonthYear(leftCalendar.month, leftCalendar.year) }}
          </div>
          <div class="nav-buttons">
            <b-button
              variant="link"
              class="nav-btn"
              @click="nextMonth(0)"
            >
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </b-button>
            <b-button
              variant="link"
              class="nav-btn"
              @click="nextYear(0)"
            >
              <feather-icon
                icon="ChevronsRightIcon"
                size="18"
              />
            </b-button>
          </div>
        </div>

        <div class="calendar-grid">
          <div class="weekday-header">
            <div
              v-for="day in weekDays"
              :key="day"
              class="weekday"
            >
              {{ day }}
            </div>
          </div>
          <div class="days-grid">
            <div
              v-for="(day, index) in leftCalendarDays"
              :key="`left-${index}`"
              class="day"
              :class="getDayClass(day)"
              @click="selectDate(day)"
            >
              {{ day.date }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right Calendar -->
      <div class="calendar">
        <div class="calendar-header">
          <div class="nav-buttons">
            <b-button
              variant="link"
              class="nav-btn"
              @click="previousYear(1)"
            >
              <feather-icon
                icon="ChevronsLeftIcon"
                size="18"
              />
            </b-button>
            <b-button
              variant="link"
              class="nav-btn"
              @click="previousMonth(1)"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </b-button>
          </div>
          <div class="month-year">
            {{ formatMonthYear(rightCalendar.month, rightCalendar.year) }}
          </div>
          <div class="nav-buttons">
            <b-button
              variant="link"
              class="nav-btn"
              @click="nextMonth(1)"
            >
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </b-button>
            <b-button
              variant="link"
              class="nav-btn"
              @click="nextYear(1)"
            >
              <feather-icon
                icon="ChevronsRightIcon"
                size="18"
              />
            </b-button>
          </div>
        </div>

        <div class="calendar-grid">
          <div class="weekday-header">
            <div
              v-for="day in weekDays"
              :key="day"
              class="weekday"
            >
              {{ day }}
            </div>
          </div>
          <div class="days-grid">
            <div
              v-for="(day, index) in rightCalendarDays"
              :key="`right-${index}`"
              class="day"
              :class="getDayClass(day)"
              @click="selectDate(day)"
            >
              {{ day.date }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Date Range Display -->
    <div class="date-range-display">
      <div class="date-range-text d-flex justify-content-between w-75">
        {{ formattedStartDate }}
        <feather-icon
          icon="ArrowRightIcon"
          size="16"
        />
        {{ formattedEndDate }}
      </div>
      <feather-icon
        icon="CalendarIcon"
        size="20"
        class="calendar-icon"
      />
    </div>
  </div>
</template>

<script>
import {
  BButton,
} from 'bootstrap-vue'
import moment from 'moment'

export default {
  name: 'DateRangePicker',
  components: {
    BButton,
  },
  props: {
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
  },
  data() {
    return {
      weekDays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
      leftCalendar: {
        month: 0,
        year: 2022,
      },
      rightCalendar: {
        month: 1,
        year: 2022,
      },
      selectedStart: null,
      selectedEnd: null,
      hoverDate: null,
    }
  },
  computed: {
    leftCalendarDays() {
      return this.generateCalendarDays(this.leftCalendar.month, this.leftCalendar.year)
    },
    rightCalendarDays() {
      return this.generateCalendarDays(this.rightCalendar.month, this.rightCalendar.year)
    },
    formattedStartDate() {
      return this.selectedStart ? moment(this.selectedStart).format('DD MMM YYYY') : 'DD/MM/YYYY'
    },
    formattedEndDate() {
      return this.selectedEnd ? moment(this.selectedEnd).format('DD MMM YYYY') : 'DD/MM/YYYY'
    },
  },
  watch: {
    startDate: {
      handler(newVal) {
        if (newVal) {
          this.selectedStart = moment(newVal).toDate()
        } else {
          this.selectedStart = null
        }
      },
      immediate: true,
    },
    endDate: {
      handler(newVal) {
        if (newVal) {
          this.selectedEnd = moment(newVal).toDate()
        } else {
          this.selectedEnd = null
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.startDate) {
      this.selectedStart = moment(this.startDate).toDate()
    }
    if (this.endDate) {
      this.selectedEnd = moment(this.endDate).toDate()
    }

    // Initialize calendars
    const today = moment()
    this.leftCalendar.month = today.month()
    this.leftCalendar.year = today.year()
    this.rightCalendar.month = today.clone().add(1, 'month').month()
    this.rightCalendar.year = today.clone().add(1, 'month').year()
  },
  methods: {
    generateCalendarDays(month, year) {
      const days = []
      const firstDay = moment([year, month, 1])
      const lastDay = firstDay.clone().endOf('month')
      const startDay = firstDay.clone().startOf('isoWeek')
      const endDay = lastDay.clone().endOf('isoWeek')

      const day = startDay.clone()
      while (day.isSameOrBefore(endDay)) {
        days.push({
          date: day.date(),
          month: day.month(),
          year: day.year(),
          fullDate: day.toDate(),
          isCurrentMonth: day.month() === month,
        })
        day.add(1, 'day')
      }

      return days
    },
    formatMonthYear(month, year) {
      return moment([year, month]).format('MMMM YYYY')
    },
    previousMonth(calendarIndex) {
      if (calendarIndex === 0) {
        const newDate = moment([this.leftCalendar.year, this.leftCalendar.month]).subtract(1, 'month')
        this.leftCalendar.month = newDate.month()
        this.leftCalendar.year = newDate.year()
      } else {
        const newDate = moment([this.rightCalendar.year, this.rightCalendar.month]).subtract(1, 'month')
        this.rightCalendar.month = newDate.month()
        this.rightCalendar.year = newDate.year()
      }
    },
    nextMonth(calendarIndex) {
      if (calendarIndex === 0) {
        const newDate = moment([this.leftCalendar.year, this.leftCalendar.month]).add(1, 'month')
        this.leftCalendar.month = newDate.month()
        this.leftCalendar.year = newDate.year()
      } else {
        const newDate = moment([this.rightCalendar.year, this.rightCalendar.month]).add(1, 'month')
        this.rightCalendar.month = newDate.month()
        this.rightCalendar.year = newDate.year()
      }
    },
    previousYear(calendarIndex) {
      if (calendarIndex === 0) {
        this.leftCalendar.year -= 1
      } else {
        this.rightCalendar.year -= 1
      }
    },
    nextYear(calendarIndex) {
      if (calendarIndex === 0) {
        this.leftCalendar.year += 1
      } else {
        this.rightCalendar.year += 1
      }
    },
    selectDate(day) {
      if (!day.isCurrentMonth) return

      const selectedDate = day.fullDate

      if (!this.selectedStart || (this.selectedStart && this.selectedEnd)) {
        // Start new selection
        this.selectedStart = selectedDate
        this.selectedEnd = null
      } else if (moment(selectedDate).isBefore(this.selectedStart)) {
        // If selected date is before start, make it the new start
        this.selectedStart = selectedDate
      } else {
        // Set end date
        this.selectedEnd = selectedDate
        this.$emit('change', {
          startDate: moment(this.selectedStart).format('YYYY-MM-DD'),
          endDate: moment(this.selectedEnd).format('YYYY-MM-DD'),
        })
      }
    },
    getDayClass(day) {
      const classes = []

      if (!day.isCurrentMonth) {
        classes.push('other-month')
        return classes
      }

      const dayMoment = moment(day.fullDate)
      const isSelected = (this.selectedStart && dayMoment.isSame(this.selectedStart, 'day'))
        || (this.selectedEnd && dayMoment.isSame(this.selectedEnd, 'day'))

      const isInRange = this.selectedStart && this.selectedEnd
        && dayMoment.isAfter(this.selectedStart, 'day')
        && dayMoment.isBefore(this.selectedEnd, 'day')

      if (isSelected) {
        classes.push('selected')
      }

      if (isInRange) {
        classes.push('in-range')
      }

      return classes
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/include';

.date-range-picker {
  background: #283046;
  border-radius: 8px;
  padding: 1.5rem;
  color: #b4b7bd;
  max-width: 800px;
  margin: 0 auto;
}

.calendar-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1.5rem;
}

.calendar {
  .calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    .nav-buttons {
      display: flex;
      gap: 0.25rem;
    }

    .nav-btn {
      color: #b4b7bd;
      padding: 0.25rem;

      &:hover {
        color: #fff;
      }
    }

    .month-year {
      font-size: 1rem;
      font-weight: 500;
      color: #d0d2d6;
    }
  }
}

.calendar-grid {
  .weekday-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.25rem;
    margin-bottom: 0.5rem;

    .weekday {
      text-align: center;
      padding: 0.5rem 0;
      font-size: 0.875rem;
      color: #676d7d;
      font-weight: 500;
    }
  }

  .days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.25rem;

    .day {
      aspect-ratio: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      font-size: 0.9rem;
      cursor: pointer;
      transition: all 0.2s ease;
      color: #d0d2d6;

      &:hover:not(.other-month) {
        background: rgba(115, 103, 240, 0.2);
      }

      &.other-month {
        color: #4b5563;
        cursor: default;
      }

      &.selected {
        background: #7367f0;
        color: #fff;
        font-weight: 600;
      }

      &.in-range {
        background: rgba(115, 103, 240, 0.15);
      }
    }
  }
}

.date-range-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #161d31;
  padding: 0.875rem 1.25rem;
  border-radius: 6px;
  border: 1px solid #3b4253;

  .date-range-text {
    font-size: 0.95rem;
    color: #d0d2d6;
    font-weight: 500;
  }

  .calendar-icon {
    color: #676d7d;
  }
}

// Light Theme Support
body:not(.dark-layout) {
  .date-range-picker {
    background: #fff;
    color: #6e6b7b;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  }

  .calendar {
    .calendar-header {
      .nav-btn {
        color: #6e6b7b;

        &:hover {
          color: #5e5873;
        }
      }

      .month-year {
        color: #5e5873;
      }
    }
  }

  .calendar-grid {
    .weekday-header .weekday {
      color: #b9b9c3;
    }

    .days-grid .day {
      color: #5e5873;

      &:hover:not(.other-month) {
        background: rgba(115, 103, 240, 0.12);
      }

      &.other-month {
        color: #b9b9c3;
      }

      &.selected {
        background: #7367f0;
        color: #fff;
      }

      &.in-range {
        background: rgba(115, 103, 240, 0.08);
      }
    }
  }

  .date-range-display {
    background: #f8f8f8;
    border-color: #ebe9f1;

    .date-range-text {
      color: #5e5873;
    }

    .calendar-icon {
      color: #b9b9c3;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .calendar-container {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .date-range-picker {
    padding: 1rem;
  }
}
</style>
